import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { useCallback, useRef, useState } from 'react'
import { useSaleManager } from '../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks'
import {
  selectLineItems,
  selectSaleAttributesHasFulfilment,
  selectSaleId,
  updateLineItems,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import { selectActiveOutletId } from '../../../../../state/slices/posState/posStateSlice'
import { useCrossLocationFulfilmentGate } from '../../../sale-line-item/fulfilment/OutletSelect'
import type { ClfOversellOutletSelections } from './ClfOversellWarningModal'
import { findClfOversellLines } from './clf-oversell-guard'
import type { ClfOversellLine } from './clf-oversell-guard'

interface UseClfOversellWarningOptions {
  onContinue: () => void
}

export const useClfOversellWarning = ({
  onContinue,
}: UseClfOversellWarningOptions) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const lineItems = useAppSelector(selectLineItems)
  const saleId = useAppSelector(selectSaleId)
  const saleHasFulfilment = useAppSelector(selectSaleAttributesHasFulfilment)
  const activeOutletId = useAppSelector(selectActiveOutletId)
  const isCheckingRef = useRef(false)
  const [isChecking, setIsChecking] = useState(false)
  const [oversellLines, setOversellLines] = useState<ClfOversellLine[]>([])
  const { canUseCrossLocationFulfilment } = useCrossLocationFulfilmentGate()

  const continueWithoutWarning = useCallback(() => {
    setOversellLines([])
    onContinue()
  }, [onContinue])

  const checkForOversells = useCallback(async () => {
    if (isCheckingRef.current || oversellLines.length > 0) {
      return
    }

    const lineItemsWithEditableOutlet =
      saleId && saleHasFulfilment
        ? lineItems.filter(lineItem => !lineItem.confirmed)
        : lineItems

    if (
      !canUseCrossLocationFulfilment ||
      lineItemsWithEditableOutlet.length === 0
    ) {
      continueWithoutWarning()
      return
    }

    isCheckingRef.current = true
    setIsChecking(true)
    let shouldContinue = false

    try {
      const refreshedOversellLines = await findClfOversellLines(
        lineItemsWithEditableOutlet
      )

      if (refreshedOversellLines?.length) {
        setOversellLines(refreshedOversellLines)
      } else {
        shouldContinue = true
      }
    } catch {
      shouldContinue = true
    } finally {
      isCheckingRef.current = false
      setIsChecking(false)
    }

    if (shouldContinue) {
      continueWithoutWarning()
    }
  }, [
    continueWithoutWarning,
    lineItems,
    oversellLines.length,
    canUseCrossLocationFulfilment,
    saleHasFulfilment,
    saleId,
  ])

  const confirmOversell = useCallback(
    async (outletSelections: ClfOversellOutletSelections) => {
      try {
        const updatedLineItems = lineItems
          .filter(lineItem => {
            const selection = outletSelections[lineItem.localId]

            return (
              selection &&
              lineItem.fulfilmentOutletId !== selection.fulfilmentOutletId
            )
          })
          .map(lineItem => ({
            ...lineItem,
            fulfilmentOutletId:
              outletSelections[lineItem.localId].fulfilmentOutletId,
          }))

        if (updatedLineItems.length > 0) {
          dispatch(updateLineItems(updatedLineItems))
          try {
            await saleManager.saveSale()
          } catch {
            dispatch(
              updateLineItems(
                updatedLineItems.map(lineItem => ({
                  ...lineItem,
                  fulfilmentOutletId: activeOutletId,
                }))
              )
            )
            ToastNotifications.negative(
              t(`Couldn't change fulfillment outlet. Try again.`, {
                _context: 'Toast message',
              })
            )
          }
        }
      } catch {
        // Intentionally swallow synchronous failures because the warning is fail-open.
      } finally {
        continueWithoutWarning()
      }
    },
    [
      activeOutletId,
      continueWithoutWarning,
      dispatch,
      lineItems,
      saleManager,
      t,
    ]
  )

  const closeOversellWarning = useCallback(() => setOversellLines([]), [])

  return {
    checkForOversells,
    closeOversellWarning,
    confirmOversell,
    continueWithoutWarning,
    isChecking,
    oversellLines,
  }
}
